/**
 * gestureSequencer — plays a sentence's gestures one after another instead of
 * last-write-wins.
 *
 * Convorcher can attach SEVERAL animations to one TextOut sentence (each with
 * its own generated tracks). The playback channels are single-slot by design
 * (GestureChannel's additive envelope, __DIRECT_PLAY__'s override hold), so
 * firing them all at once means only the last one survives — and before this
 * module the renderer simply dropped everything past animations[0]. The
 * sequencer is the missing ordering layer: EventRuntime's playGesture node
 * enqueues a start-thunk per gesture, and the queue advances on wall-clock
 * duration, mirroring how GestureChannel itself times its envelope.
 *
 * Deliberately dumb: no per-frame work, no completion plumbing into the mixer
 * — each item carries its own durationMs (derived from the clip's frames/fps
 * by the caller) and the next item starts when it elapses. An empty, idle
 * queue starts the incoming gesture SYNCHRONOUSLY in the same tick, so the
 * single-gesture case is byte-identical to the pre-sequencer behavior.
 *
 * clearGestureQueue() drops PENDING items only — the currently playing clip
 * stays owned by its channel (GestureChannel / __DIRECT_PLAY__), whose own
 * teardown semantics (root-motion commit, envelope fade) must not be
 * preempted here. SentenceController.reset() calls it so a new response or a
 * barge-in never inherits a stale gesture backlog.
 */

// Module-level state — same singleton pattern as GestureChannel.
let _pending = []      // [{ durationMs, start }]
let _activeUntil = 0   // performance.now() deadline of the running item; 0 = idle
let _timer = null
// Bumped by clearGestureQueue; a scheduled advance captured under an older
// epoch is a no-op, so a cleared queue can't resurrect via a stale timeout.
let _epoch = 0

const MAX_QUEUE = 8 // backstop — a runaway server payload can't stack minutes of motion

function _startItem(item) {
  const now = performance.now()
  _activeUntil = now + item.durationMs
  try {
    item.start()
  } catch (e) {
    // A throwing start must not wedge the queue — advance as scheduled.
    console.warn('[gestureSequencer] gesture start threw:', e)
  }
  const epoch = _epoch
  _timer = setTimeout(() => {
    if (epoch !== _epoch) return
    _timer = null
    _activeUntil = 0
    const next = _pending.shift()
    if (next) _startItem(next)
  }, item.durationMs)
}

/**
 * Enqueue a gesture. Starts immediately (same tick) when nothing is playing.
 * @param {object} item { durationMs: number, start: () => void }
 */
export function enqueueGesture(item) {
  if (!item || typeof item.start !== 'function') return
  const durationMs = Number.isFinite(item.durationMs) && item.durationMs > 0
    ? item.durationMs
    : 2500
  const entry = { durationMs, start: item.start }
  if (_activeUntil === 0) {
    _startItem(entry)
    return
  }
  if (_pending.length >= MAX_QUEUE) {
    console.warn(`[gestureSequencer] queue full (${MAX_QUEUE}) — dropping gesture`)
    return
  }
  _pending.push(entry)
}

/** Drop all pending gestures (the running one keeps its channel's lifecycle). */
export function clearGestureQueue() {
  _epoch++
  _pending = []
  if (_timer !== null) {
    clearTimeout(_timer)
    _timer = null
  }
  _activeUntil = 0
}

// Dev / test hook, following the __GESTURE_CHANNEL__ convention.
if (typeof window !== 'undefined') {
  window.__GESTURE_SEQUENCER__ = {
    get pending() { return _pending.length },
    get active() { return _activeUntil !== 0 },
    enqueue: enqueueGesture,
    clear: clearGestureQueue,
  }
}
